import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './Skeleton';
import { Sparkles, TrendingUp, BarChart3, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDate } from '../lib/utils';
import api from '../lib/api';
import { toast } from 'sonner';

export default function ProjectInsightsTab({ project }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadInsights();
  }, [project.id]);

  const loadInsights = async () => {
    try {
      const response = await api.get('/insights', { params: { project_id: project.id } });
      setInsights(response.data);
    } catch (error) {
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/insights/generate', {
        project_id: project.id,
        type: 'weekly_summary'
      });
      setInsights([response.data, ...insights]);
      toast.success('Insight generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate insight');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-insights-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-1">AI Insights</h2>
          <p className="text-sm text-muted-foreground">
            Automatically generated summaries and analysis
          </p>
        </div>
        <Button
          onClick={handleGenerateInsight}
          disabled={generating}
          data-testid="generate-insight-btn"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Weekly Summary
            </>
          )}
        </Button>
      </div>

      {insights.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 mb-4">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No insights yet</h3>
          <p className="text-muted-foreground mb-6">
            Generate your first AI-powered weekly summary to see activity patterns and highlights
          </p>
          <Button onClick={handleGenerateInsight} disabled={generating}>
            <Sparkles className="h-4 w-4 mr-2" />
            Generate First Insight
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {insights.map((insight) => {
            const { highlights = [], chart_data = [], statistics = {} } = insight.ui_content || {};

            return (
              <Card key={insight.id} className="p-6" data-testid={`insight-${insight.id}`}>
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{insight.title}</h3>
                        <Badge variant="secondary">{insight.type.replace('_', ' ')}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Generated {formatDate(insight.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  {insight.content && (
                    <p className="text-sm">{insight.content}</p>
                  )}

                  {/* Highlights */}
                  {highlights.length > 0 && (
                    <div className="bg-muted p-4 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Key Highlights
                      </h4>
                      <ul className="space-y-2">
                        {highlights.map((highlight, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <span className="text-primary">•</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Statistics */}
                  {statistics.total_events && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">Total Events</p>
                        <p className="text-2xl font-semibold">{statistics.total_events}</p>
                      </div>
                      {statistics.by_type && (
                        Object.entries(statistics.by_type).map(([type, count]) => (
                          <div key={type} className="bg-muted p-3 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1 capitalize">{type.replace('_', ' ')}</p>
                            <p className="text-2xl font-semibold">{count}</p>
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* Chart */}
                  {chart_data && chart_data.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Activity Over Time
                      </h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={chart_data}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="events" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
